import { type CanvasTokenNode } from '../slices/createCanvasTokenNodeSlice';

/**
 * Merges canvas token nodes from the canvas store and chat history store.
 * Duplicates are identified by comparing media URLs (image/video nodes).
 * When a duplicate is found, the canvas node wins.
 * Text nodes are always included from both sources since they have no media URL.
 */
export function mergeCanvasTokenNodes(
  canvasNodes: Record<string, CanvasTokenNode>,
  chatHistoryNodes: Record<string, CanvasTokenNode>,
  composerNodes?: Record<string, CanvasTokenNode>,
): Record<string, CanvasTokenNode> {
  const canvasEmpty = Object.keys(canvasNodes).length === 0;
  const historyEmpty = Object.keys(chatHistoryNodes).length === 0;
  const composerEmpty = !composerNodes || Object.keys(composerNodes).length === 0;

  if (canvasEmpty && historyEmpty && composerEmpty) {
    return canvasNodes;
  }

  // Collect all media URLs already seen (canvas wins first, then history)
  const seenMediaUrls = new Set<string>();

  // Build a map of history node timestamps keyed by media URL so we can
  // inherit them when a canvas node wins but hasn't been persisted yet
  const historyTimestamps = new Map<string, string>();
  for (const node of Object.values(chatHistoryNodes)) {
    if ((node.type === 'image' || node.type === 'video') && node.updatedAt) {
      historyTimestamps.set(node.media.original, node.updatedAt);
    }
  }

  for (const node of Object.values(canvasNodes)) {
    if (node.type === 'image' || node.type === 'video') {
      seenMediaUrls.add(node.media.original);
    }
  }

  const merged: Record<string, CanvasTokenNode> = { ...canvasNodes };

  // Canvas nodes win dedup but may not have a backend updatedAt yet if they
  // haven't been persisted. Fall back to the history node's tool result timestamp.
  for (const [id, node] of Object.entries(merged)) {
    if ((node.type === 'image' || node.type === 'video') && !node.updatedAt) {
      const historyTimestamp = historyTimestamps.get(node.media.original);

      if (historyTimestamp) {
        merged[id] = { ...node, updatedAt: historyTimestamp };
      }
    }
  }

  for (const [id, node] of Object.entries(chatHistoryNodes)) {
    if ((node.type === 'image' || node.type === 'video') && seenMediaUrls.has(node.media.original)) {
      continue;
    }
    if (node.type === 'image' || node.type === 'video') {
      seenMediaUrls.add(node.media.original);
    }
    merged[id] = node;
  }

  if (!composerEmpty) {
    for (const [id, node] of Object.entries(composerNodes)) {
      if (merged[id]) {
        continue;
      }

      merged[id] = node;
    }
  }

  return merged;
}
